/**
 * Menu do header e footer flutuante do modal V2 (reutiliza lógica de impressão do footer oficial).
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Forward, History, Lightbulb, Paperclip, Waypoints, Undo2, Loader2 } from 'lucide-react';
import { generateOSPdfV2Download, generateOSPdfV2FromOrder } from '../../new-os/utils/osPrintPdf';

export function OrderDetailsModalV2HeaderMenu({
  viewMode,
  onSelectLogs,
  onSelectHistorico,
  onSelectSugestao,
  onSelectAnexos,
  canViewAnexos = false,
  onCloseSubView,
  onEncaminharChamado,
  canEncaminharChamado,
  canReturnToPendente = false,
  onReturnToPendente,
  returnToPendenteBusy = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const isMenuActive = viewMode === 'logs' || viewMode === 'historico' || viewMode === 'sugestao';
  const activeLabel =
    viewMode === 'logs' ? 'Logs' : viewMode === 'historico' ? 'Histórico' : viewMode === 'sugestao' ? 'Sugestão' : null;
  const anexosActive = viewMode === 'anexos';

  if (isMenuActive && onCloseSubView) {
    return (
      <div className="odv2-header-actions">
        {canViewAnexos && onSelectAnexos ? (
          <button type="button" className="odv2-header-menu-btn" onClick={onSelectAnexos}>
            <Paperclip size={14} strokeWidth={2} aria-hidden />
            Anexos
          </button>
        ) : null}
        <button type="button" className="odv2-header-menu-btn odv2-header-menu-btn--active" onClick={onCloseSubView}>
          {activeLabel}
          <span aria-hidden>×</span>
        </button>
      </div>
    );
  }

  return (
    <div className="odv2-header-actions">
      {canViewAnexos && onSelectAnexos ? (
        <button
          type="button"
          className={`odv2-header-menu-btn${anexosActive ? ' odv2-header-menu-btn--active' : ''}`}
          aria-pressed={anexosActive}
          onClick={onSelectAnexos}
        >
          <Paperclip size={14} strokeWidth={2} aria-hidden />
          Anexos
        </button>
      ) : null}
    <div className="odv2-header-menu-wrap" ref={ref}>
      <button
        type="button"
        className="odv2-header-menu-btn"
        aria-expanded={open}
        disabled={returnToPendenteBusy}
        onClick={() => setOpen((v) => !v)}
      >
        Menu
        <ChevronDown size={14} strokeWidth={2} aria-hidden />
      </button>
      {open ? (
        <div className="odv2-dropdown odv2-dropdown--header">
          {canReturnToPendente && onReturnToPendente ? (
            <button
              type="button"
              className="odv2-dropdown-item"
              disabled={returnToPendenteBusy}
              onClick={() => {
                setOpen(false);
                onReturnToPendente();
              }}
            >
              {returnToPendenteBusy ? (
                <Loader2 size={14} strokeWidth={2} className="odv2-return-pendente-spin" aria-hidden />
              ) : (
                <Undo2 size={14} strokeWidth={2} aria-hidden />
              )}
              Retornar para pendência
            </button>
          ) : null}
          {canEncaminharChamado && onEncaminharChamado ? (
            <button
              type="button"
              className="odv2-dropdown-item"
              onClick={() => {
                const menuEl = ref.current?.querySelector('.odv2-dropdown--header');
                const rect =
                  menuEl?.getBoundingClientRect?.() ||
                  ref.current?.getBoundingClientRect?.() ||
                  null;
                onEncaminharChamado(
                  rect
                    ? {
                        x: Math.round(rect.left),
                        y: Math.round(rect.top),
                        left: rect.left,
                        top: rect.top,
                        bottom: rect.bottom,
                        right: rect.right,
                        width: rect.width,
                        height: rect.height,
                      }
                    : null
                );
                setOpen(false);
              }}
            >
              <Forward size={14} strokeWidth={2} aria-hidden />
              Encaminhar chamado
            </button>
          ) : null}
          {onSelectLogs ? (
            <button
              type="button"
              className="odv2-dropdown-item"
              onClick={() => {
                onSelectLogs();
                setOpen(false);
              }}
            >
              <History size={14} strokeWidth={2} aria-hidden />
              Logs
            </button>
          ) : null}
          {onSelectHistorico ? (
            <button
              type="button"
              className="odv2-dropdown-item"
              onClick={() => {
                onSelectHistorico();
                setOpen(false);
              }}
            >
              <Waypoints size={14} strokeWidth={2} aria-hidden />
              Histórico do processo
            </button>
          ) : null}
          {onSelectSugestao ? (
            <button
              type="button"
              className="odv2-dropdown-item"
              onClick={() => {
                onSelectSugestao();
                setOpen(false);
              }}
            >
              <Lightbulb size={14} strokeWidth={2} aria-hidden />
              Sugestão de solução
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
    </div>
  );
}

export function OrderDetailsModalV2FloatingFooter({
  order,
  defeitoData,
  onClose,
  showAddAnotacaoButton,
  onAddAnotacao,
}) {
  const [showPrint, setShowPrint] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const printRef = useRef(null);

  const orderForPdf = React.useMemo(() => {
    if (!order) return order;
    const nome = defeitoData?.defeito != null && defeitoData.defeito !== 'Não informado' ? defeitoData.defeito : undefined;
    const obs =
      defeitoData?.observacao != null && defeitoData.observacao !== 'Não informado' ? defeitoData.observacao : undefined;
    if (nome === undefined && obs === undefined) return order;
    return { ...order, defeitoNome: nome, defeitoObservacao: obs };
  }, [order, defeitoData]);

  useEffect(() => {
    const onDoc = (e) => {
      if (printRef.current && !printRef.current.contains(e.target) && !loadingAction) setShowPrint(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [loadingAction]);

  const handleGerarPDF = async () => {
    if (!order) return;
    setLoadingAction('pdf');
    try {
      await generateOSPdfV2Download(orderForPdf);
    } finally {
      setLoadingAction(null);
      setShowPrint(false);
    }
  };

  const handleImprimir = async () => {
    if (!order) return;
    setLoadingAction('print');
    try {
      await generateOSPdfV2FromOrder(orderForPdf, { openForPrint: true });
    } finally {
      setLoadingAction(null);
      setShowPrint(false);
    }
  };

  return (
    <footer className="odv2-footer-float">
      <div className="odv2-footer-float-inner">
        {showAddAnotacaoButton && onAddAnotacao ? (
          <button type="button" className="odv2-pill-btn" onClick={onAddAnotacao}>
            Adicionar Nota
          </button>
        ) : null}
        <div className="odv2-pill-wrap" ref={printRef}>
          <button type="button" className="odv2-pill-btn" onClick={() => setShowPrint((v) => !v)}>
            Impressão
          </button>
          {showPrint ? (
            <div className="odv2-dropdown odv2-dropdown--print-up">
              <button type="button" className="odv2-dropdown-item" disabled={!!loadingAction} onClick={handleGerarPDF}>
                {loadingAction === 'pdf' ? (
                  <>
                    <i className="bi bi-arrow-repeat odv2-impressao-spin" aria-hidden />
                    Gerando PDF…
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-pdf" aria-hidden />
                    Gerar PDF
                  </>
                )}
              </button>
              <button type="button" className="odv2-dropdown-item" disabled={!!loadingAction} onClick={handleImprimir}>
                {loadingAction === 'print' ? (
                  <>
                    <i className="bi bi-arrow-repeat odv2-impressao-spin" aria-hidden />
                    Imprimindo…
                  </>
                ) : (
                  <>
                    <i className="bi bi-printer" aria-hidden />
                    Imprimir
                  </>
                )}
              </button>
            </div>
          ) : null}
        </div>
        <button type="button" className="odv2-pill-btn" onClick={onClose}>
          Sair
        </button>
      </div>
    </footer>
  );
}
